import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/auth-store';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation();
  const { isAuthenticated, isLoading, currentTenantId, tenants, getConnectedTenants } =
    useAuthStore();

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Get only connected tenants (not disconnected)
  const connectedTenants = getConnectedTenants();

  // If current tenant is disconnected, redirect to company selection
  if (currentTenantId) {
    const currentTenant = tenants.find((t) => t.id === currentTenantId);
    if (currentTenant?.quickbooks?.disconnectedAt) {
      return <Navigate to="/companies" replace />;
    }
  }

  // Redirect to company selection if authenticated but no tenant selected
  if (!currentTenantId && connectedTenants.length === 0) {
    // No connected tenants - need to connect or reconnect
    return <Navigate to="/companies" replace />;
  }

  // If no tenant selected but has connected tenants, redirect to company selection
  if (!currentTenantId && connectedTenants.length > 0) {
    return <Navigate to="/companies" replace />;
  }

  return <>{children}</>;
}
